/**
 * Tier card — the bahi-khaata centerpiece. Devanagari title + English kicker
 * + the line that earns the tier. View-transition-named.
 */
import { store } from '../store.ts';
import { TIER_DISPLAY, formatINR } from '../types.ts';
import type { Tier } from '../types.ts';

const TIER_TONE: Record<Tier, { ink: string; wash: string; rule: string }> = {
  aam_aadmi:    { ink: 'oklch(38% 0.06 50)',    wash: 'oklch(60% 0.040 50 / 0.08)', rule: 'oklch(46% 0.080 50)' },
  madhyam_varg: { ink: 'oklch(40% 0.120 60)',   wash: 'oklch(54% 0.130 60 / 0.10)', rule: 'oklch(56% 0.140 60)' },
  khaata_peeta: { ink: 'oklch(40% 0.130 35)',   wash: 'oklch(54% 0.150 32 / 0.12)', rule: 'oklch(54% 0.160 32)' },
  khaandani:    { ink: 'oklch(38% 0.150 22)',   wash: 'oklch(50% 0.170 22 / 0.14)', rule: 'oklch(48% 0.180 22)' },
  sheth:        { ink: 'oklch(36% 0.180 20)',   wash: 'oklch(48% 0.200 20 / 0.16)', rule: 'oklch(46% 0.200 20)' },
};

export class HisaabTierCard extends HTMLElement {
  #shadow = this.attachShadow({ mode: 'open' });
  #unsub?: () => void;

  connectedCallback(): void {
    this.#unsub = store.subscribe((s) => {
      if (s.summary) this.#render(s.summary.insights.tier, s.summary.totals.monthly_avg);
    });
  }

  disconnectedCallback(): void {
    this.#unsub?.();
  }

  #render(tier: Tier, monthlyAvg: number): void {
    const tone = TIER_TONE[tier];
    const meta = TIER_DISPLAY[tier];
    const tierIndex = ['aam_aadmi','madhyam_varg','khaata_peeta','khaandani','sheth'].indexOf(tier);
    this.#shadow.innerHTML = /* html */ `
<style>
:host {
  display: block;
  view-transition-name: tier-card;
}
.card {
  position: relative;
  background: var(--paper-warm);
  border: 1px solid var(--rule-strong);
  border-radius: 4px;
  padding: 30px 26px 32px;
  overflow: hidden;
  isolation: isolate;
  box-shadow:
    0 1px 0 var(--paper-fold),
    0 6px 18px -12px oklch(40% 0.06 50 / 0.22);
  transition: border-color 320ms cubic-bezier(0.32, 0.72, 0, 1);
}
/* faint tier-coloured wash */
.card::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(165deg, ${tone.wash} 0%, transparent 55%);
  z-index: -1;
}
/* corner crest mark */
.card::after {
  content: '';
  position: absolute;
  top: 0; left: 0;
  width: 28px;
  height: 28px;
  border-top: 1px solid ${tone.rule};
  border-left: 1px solid ${tone.rule};
  opacity: 0.7;
}
.label-row {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  font-family: var(--mono);
  font-size: 9.5px;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  color: var(--ink-faint);
  padding-bottom: 16px;
  border-bottom: 1px solid var(--rule-hair);
  margin-bottom: 18px;
}
.label-row .pos {
  color: ${tone.ink};
  font-weight: 700;
}
.deva {
  font-family: var(--hindi);
  font-size: clamp(36px, 9.5cqw, 56px);
  font-weight: 400;
  color: ${tone.ink};
  letter-spacing: -0.005em;
  line-height: 0.92;
  text-wrap: balance;
}
.name {
  font-family: var(--display);
  font-style: italic;
  font-weight: 700;
  font-size: clamp(20px, 5.5cqw, 28px);
  color: var(--ink);
  margin-top: 6px;
  letter-spacing: -0.005em;
}
.rule {
  width: 56px;
  height: 1px;
  background: ${tone.rule};
  opacity: 0.7;
  margin: 18px 0 16px;
}
.tagline {
  font-family: var(--display);
  font-style: italic;
  font-size: 20px;
  color: var(--ink);
  font-weight: 500;
  text-wrap: balance;
  line-height: 1.35;
  letter-spacing: 0;
}
.kicker {
  font-family: var(--mono);
  font-size: 10.5px;
  letter-spacing: 0.22em;
  text-transform: uppercase;
  color: ${tone.ink};
  margin-top: 14px;
  opacity: 0.85;
}
.kicker .deva-accent {
  font-family: var(--hindi);
  font-size: 13px;
  text-transform: none;
  letter-spacing: 0;
  color: ${tone.ink};
}
.monthly {
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-top: 26px;
  padding-top: 20px;
  border-top: 1px solid var(--rule-hair);
}
.monthly-num {
  font-family: var(--display);
  font-style: italic;
  font-size: clamp(42px, 12cqw, 64px);
  font-weight: 700;
  color: var(--crimson);
  letter-spacing: -0.025em;
  line-height: 1;
  font-feature-settings: 'tnum' 1, 'lnum' 1, 'ss01' 1;
}
.monthly-cap {
  font-family: var(--mono);
  font-size: 10px;
  color: var(--ink-faint);
  letter-spacing: 0.3em;
  text-transform: uppercase;
}
/* the rung — five-dot tier indicator */
.rung {
  display: flex;
  gap: 8px;
  margin-top: 18px;
}
.rung-dot {
  width: 9px;
  height: 9px;
  border-radius: 50%;
  border: 1px solid ${tone.rule};
  background: transparent;
  transition: background 360ms cubic-bezier(0.32, 0.72, 0, 1);
}
.rung-dot.filled {
  background: ${tone.ink};
}
.rung-dot.current {
  box-shadow: 0 0 0 3px ${tone.wash};
}
</style>
<article class="card">
  <div class="label-row">
    <span>Aapki kahaani</span>
    <span class="pos">${tierIndex + 1} / 5</span>
  </div>
  <div class="deva">${meta.deva}</div>
  <div class="name">${meta.name}</div>
  <div class="rule"></div>
  <div class="tagline">${meta.tagline}</div>
  <div class="kicker">${meta.kicker.replace(/([ऀ-ॿ]+)/g, '<span class="deva-accent">$1</span>')}</div>
  <div class="rung" role="img" aria-label="Tier ${tierIndex + 1} of 5">
    ${[0,1,2,3,4].map(i => `<span class="rung-dot ${i <= tierIndex ? 'filled' : ''} ${i === tierIndex ? 'current' : ''}"></span>`).join('')}
  </div>
  <div class="monthly">
    <span class="monthly-num">${formatINR(monthlyAvg)}</span>
    <span class="monthly-cap">/ month avg</span>
  </div>
</article>
`;
  }
}

customElements.define('hisaab-tier-card', HisaabTierCard);
